import { useState, useEffect, useCallback } from 'react';

export default function UpgradeCountdown() {
  const [timeLeft, setTimeLeft] = useState('');
  const [isExpired, setIsExpired] = useState(false);
  const [blocksUntilUpgrade, setBlocksUntilUpgrade] = useState(null);
  const [isLive, setIsLive] = useState(false);
  const targetDate = '2025-11-15T12:00:00Z';
  const apiUrl = 'https://avalanche.cash/api/info/XEC';
  const preDateText = 'Avalanche Pre-Consensus coming to mainnet!';
  const postDateText =
    'Upgrade complete! Avalanche Pre-Consensus now live on mainnet!';

  // Fetch blocks until upgrade from API
  const fetchBlocksUntilUpgrade = useCallback(async () => {
    try {
      const response = await fetch(apiUrl);
      if (!response.ok) {
        throw new Error('API request failed');
      }
      const data = await response.json();
      const blocks = data.blocksUntilUpgrade;

      if (blocks !== null && blocks !== undefined) {
        if (blocks < 0) {
          setIsLive(true);
          setBlocksUntilUpgrade(0);
        } else {
          setBlocksUntilUpgrade(blocks);
        }
      } else {
        // If blocksUntilUpgrade is null, we might still be before the timestamp
        // Don't treat this as an error, just don't update the state
      }
    } catch (error) {
      console.error('Error fetching blocks until upgrade:', error);
      setIsLive(true);
    }
  }, [apiUrl]);

  // Calculate time left until target date
  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const target = new Date(targetDate).getTime();
      const difference = target - now;

      if (difference <= 0) {
        setIsExpired(true);
        return null;
      }

      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor(
        (difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
      );
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      return { days, hours, minutes, seconds };
    };

    // Calculate initial time
    const initialTimeLeft = calculateTimeLeft();
    setTimeLeft(initialTimeLeft);

    // Update every second
    const timer = setInterval(() => {
      const newTimeLeft = calculateTimeLeft();
      setTimeLeft(newTimeLeft);
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Poll API for blocks until upgrade after timestamp is reached
  useEffect(() => {
    if (isExpired && !isLive) {
      // Fetch immediately when timestamp is reached
      fetchBlocksUntilUpgrade();

      // Then poll every 5 minutes (300000 ms)
      const pollingInterval = setInterval(() => {
        fetchBlocksUntilUpgrade();
      }, 300000); // 5 minutes

      return () => clearInterval(pollingInterval);
    }
  }, [isExpired, isLive, fetchBlocksUntilUpgrade]);

  const formatTime = (value) => {
    return value.toString().padStart(2, '0');
  };

  return (
    <a
      href="https://e.cash/blog/preconsensus-pr"
      target="_blank"
      rel="noreferrer"
    >
      {isLive ? (
        <div className="countdown_bar">{postDateText}</div>
      ) : isExpired && blocksUntilUpgrade !== null ? (
        <div className="countdown_bar">
          <div>
            {blocksUntilUpgrade} block{blocksUntilUpgrade !== 1 ? 's' : ''}{' '}
            until Avalanche Pre-consensus is live!
          </div>
        </div>
      ) : isExpired ? (
        // Waiting for API response
        <div className="countdown_bar">
          <div>Waiting for block information...</div>
        </div>
      ) : (
        <div className="countdown_bar">
          <div>{preDateText}</div>
          {timeLeft && (
            <div className="countdown_ctn">
              <span className="rounded bg-white/20 px-1.5 py-0.5">
                {formatTime(timeLeft.days)}d
              </span>
              <span className="rounded bg-white/20 px-1.5 py-0.5">
                {formatTime(timeLeft.hours)}h
              </span>
              <span className="rounded bg-white/20 px-1.5 py-0.5">
                {formatTime(timeLeft.minutes)}m
              </span>
              <span className="rounded bg-white/20 px-1.5 py-0.5">
                {formatTime(timeLeft.seconds)}s
              </span>
            </div>
          )}
        </div>
      )}
    </a>
  );
}
